import type { Idea, IdeaStatus } from "@/schemas/firestore/idea.schema";

export interface IdeaUiOverride {
  isCreatingDraft?: boolean;
  createdDraftPageId?: string | null;
  draftLockStartedAt?: number;
  draftLockExpiresAt?: number;
  draftLockWpType?: "posts" | "pages";
  status?: IdeaStatus;
  implemented_page_id?: string | null;
  implemented_page_type?: "post" | "page" | null;
  implemented_at?: Idea["implemented_at"];
}

export type IdeaUiState =
  | { kind: "loading"; label: "Generating" }
  | { kind: "ready"; label: "Ready" }
  | {
      kind: "creating";
      label: "Creating draft";
      pageId: string | null;
      startedAt: number | null;
      expiresAt: number | null;
      wpType: "posts" | "pages" | null;
    }
  | {
      kind: "implemented";
      label: "Implemented";
      pageId: string | null;
      pageType: "post" | "page" | null;
    }
  | { kind: "error"; label: "Failed"; message?: string }
  | { kind: "archived"; label: "Archived" };

export function applyIdeaUiOverride(
  idea: Idea,
  override?: IdeaUiOverride,
): Idea {
  if (!override) {
    return idea;
  }

  return {
    ...idea,
    ...(override.status ? { status: override.status } : {}),
    ...(override.implemented_page_id !== undefined
      ? { implemented_page_id: override.implemented_page_id }
      : {}),
    ...(override.implemented_page_type !== undefined
      ? { implemented_page_type: override.implemented_page_type }
      : {}),
    ...(override.implemented_at !== undefined
      ? { implemented_at: override.implemented_at }
      : {}),
  };
}

export function mapIdeaToUiState(
  idea: Idea,
  override?: IdeaUiOverride,
): IdeaUiState {
  if (override?.isCreatingDraft) {
    return {
      kind: "creating",
      label: "Creating draft",
      pageId: override.createdDraftPageId ?? null,
      startedAt: override.draftLockStartedAt ?? null,
      expiresAt: override.draftLockExpiresAt ?? null,
      wpType: override.draftLockWpType ?? null,
    };
  }

  const effectiveIdea = applyIdeaUiOverride(idea, override);

  switch (effectiveIdea.status) {
    case "pending":
      return { kind: "loading", label: "Generating" };
    case "completed":
      return { kind: "ready", label: "Ready" };
    case "implemented":
      return {
        kind: "implemented",
        label: "Implemented",
        pageId: effectiveIdea.implemented_page_id ?? null,
        pageType: effectiveIdea.implemented_page_type ?? null,
      };
    case "error":
      return {
        kind: "error",
        label: "Failed",
        message: effectiveIdea.error_message,
      };
    case "archived":
      return { kind: "archived", label: "Archived" };
  }
}

export function getIdeaStatusTone(uiState: IdeaUiState): string {
  switch (uiState.kind) {
    case "ready":
      return "bg-green-50 text-green-700 border-green-200";
    case "loading":
    case "creating":
      return "bg-amber-50 text-amber-700 border-amber-200";
    case "implemented":
      return "bg-blue-50 text-blue-700 border-blue-200";
    case "error":
      return "bg-red-50 text-red-700 border-red-200";
    case "archived":
      return "bg-slate-100 text-slate-600 border-slate-200";
  }
}

export function getIdeaStatusDescription(uiState: IdeaUiState): string | null {
  switch (uiState.kind) {
    case "creating":
      return "Draft generation in progress";
    case "implemented":
      if (uiState.pageType) {
        return `Implemented as ${uiState.pageType}`;
      }
      return "Linked to an existing page";
    case "error":
      return uiState.message || null;
    default:
      return null;
  }
}
